import { useEffect, useMemo } from "react";
import L from "leaflet";
import "leaflet.markercluster";
import { MapContainer, TileLayer, useMap } from "react-leaflet";

import { STATUS_META } from "../lib/format";
import type { EmployeeSummary } from "../lib/types";

// Fix default Leaflet icon paths (Vite import quirk)
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;

function ClusterLayer({ items }: { items: EmployeeSummary[] }) {
  const map = useMap();
  useEffect(() => {
    const cluster = (L as unknown as { markerClusterGroup: (o: object) => L.LayerGroup }).markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 60,
    });
    const bounds: L.LatLngTuple[] = [];
    items.forEach((emp) => {
      const lat = emp.current.lat ?? emp.home?.lat ?? null;
      const lng = emp.current.lng ?? emp.home?.lng ?? null;
      if (lat == null || lng == null) return;
      bounds.push([lat, lng]);
      const meta = STATUS_META[emp.current.status];
      const icon = L.divIcon({
        className: "tt-marker",
        html: `
          <div style="position:relative;width:24px;height:24px;display:flex;align-items:center;justify-content:center">
            <span style="position:absolute;inset:0;border-radius:9999px;background:${dotColor(emp.current.status)};opacity:0.25;animation:tt-pulse 2.5s ease-out infinite"></span>
            <span style="width:14px;height:14px;border-radius:9999px;background:${dotColor(emp.current.status)};box-shadow:0 0 0 2px #0b1220"></span>
          </div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
      });
      const marker = L.marker([lat, lng], { icon });
      marker.bindPopup(
        `<div style="min-width:220px">
           <div style="font-weight:600">${escapeHtml(emp.full_name)}</div>
           <div style="color:#94a3b8;font-size:12px">${escapeHtml(emp.title || "—")}</div>
           <div style="margin-top:6px;font-size:12px">${escapeHtml(meta.label)} · ${escapeHtml(emp.current.city || emp.current.country || "")}</div>
           <a href="/employees/${encodeURIComponent(emp.oracle_id)}" style="display:inline-block;margin-top:8px;color:#7dd3fc;text-decoration:none">Open profile →</a>
         </div>`,
      );
      cluster.addLayer(marker);
    });
    map.addLayer(cluster);
    if (bounds.length > 0) {
      try {
        map.fitBounds(L.latLngBounds(bounds), { padding: [40, 40], maxZoom: 6 });
      } catch {
        // ignore — invalid bounds
      }
    }
    return () => {
      map.removeLayer(cluster);
    };
  }, [items, map]);
  return null;
}

function dotColor(status: EmployeeSummary["current"]["status"]): string {
  switch (status) {
    case "home":
      return "#34d399";
    case "relocated":
      return "#fbbf24";
    case "in_transit":
      return "#38bdf8";
    default:
      return "#94a3b8";
  }
}

function escapeHtml(s: string | null | undefined): string {
  return (s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export default function EmployeeMap({ items }: { items: EmployeeSummary[] }) {
  const center = useMemo<[number, number]>(() => [25.276, 55.296], []);
  return (
    <div className="rounded-xl overflow-hidden ring-1 ring-[var(--color-border)] h-full min-h-[240px] w-full">
      <style>{`@keyframes tt-pulse{0%{transform:scale(0.8);opacity:0.45}100%{transform:scale(1.6);opacity:0}}`}</style>
      <MapContainer center={center} zoom={3} className="h-full w-full">
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        <ClusterLayer items={items} />
      </MapContainer>
    </div>
  );
}
